import React, { useCallback, useEffect, useMemo } from 'react';
import { usePlacesWidget } from 'react-google-autocomplete';
import { MapPin } from 'lucide-react';
import { getLang } from '@/i18n/locale';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { cn } from '@/util/cn';

export type IProps = {
  isCustom: boolean,
  positionError: boolean,
  initialLocationTitle?: string,
  setLocationCoords: (location?: { latitude: number, longitude: number, title: string }) => void,
};

// v3 location picker. Plain <input> (so we can hand its ref directly
// to usePlacesWidget) styled with shadcn-derived Tailwind classes.
// MapPin glyph sits as the prefix; "Use current location" link appears
// inline at the right when a custom place is active.

const inputClass = [
  'flex h-12 w-full rounded-md border border-input bg-background pl-10 pr-3 text-base',
  'ring-offset-background transition-colors',
  'placeholder:text-muted-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed disabled:opacity-50',
].join(' ');

export const NearbyLocationInput = ({
  positionError, isCustom, setLocationCoords, initialLocationTitle,
}: IProps) => {
  const { t } = useOTSTranslation('common');

  const { ref } = usePlacesWidget<HTMLInputElement>({
    apiKey: process.env.NEXT_PUBLIC_GOOGLE_API_5_KEY,
    onPlaceSelected: (place) => {
      const latitude = place?.geometry?.location?.lat();
      const longitude = place?.geometry?.location?.lng();
      const address = place?.formatted_address;
      if (typeof latitude !== 'undefined' && typeof longitude !== 'undefined' && typeof address !== 'undefined') {
        setLocationCoords({ latitude, longitude, title: address });
      }
    },
    language: getLang(),
  });

  const placeholder = useMemo(() => {
    if (isCustom || positionError) {
      return t('nearbyResorts.searchPlace');
    }
    return t('nearbyResorts.currentLocation');
  }, [positionError, isCustom, t]);

  useEffect(() => {
    if (ref.current) {
      ref.current.value = initialLocationTitle || '';
    }
  }, [initialLocationTitle]);

  const resetSearch = useCallback(() => {
    setLocationCoords(undefined);
  }, []);

  const checkValue = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target?.value) {
      resetSearch();
    }
  }, []);

  const showReset = initialLocationTitle && !positionError;

  return (
    <div className="space-y-2">
      <div className="relative">
        <MapPin
          size={18}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 fill-none text-muted-foreground"
          aria-hidden
        />
        <input
          ref={ref}
          className={cn(inputClass, showReset && 'pr-44')}
          placeholder={placeholder}
          onChange={checkValue}
          aria-label={placeholder}
        />
        {showReset && (
          <button
            type="button"
            onClick={resetSearch}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-1',
              'text-xs font-semibold text-primary transition-colors hover:underline',
            )}
          >
            {t('nearbyResorts.useCurrentLocation')}
          </button>
        )}
      </div>
      {positionError && (
        <p className="text-xs text-muted-foreground">
          {t('nearbyResorts.noLocation')}
        </p>
      )}
    </div>
  );
};
